/**
 * @file Store.js 主store
 */
import {createContext, useContext} from 'react';
import {makeObservable, observable, computed, toJS, action, flow} from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import {getTimbreList, getMoodList, getReourceLibList, getTTSVoice, getTTSTag, getUserInfo,
    getCloneTTSVoice, getTTSCollectionList, checkDigitalAnchorAuth, checkPreAuth, checkCanAIScript,
    controlReserveLive, getUserGuide, setUserGuide, getLivePushDetail} from '../services';
import API from '../services/api';
import {getStorage, setStorage} from '../../../utils/storage';
import {VideoStore} from './videoStore';
import {TemplateStore} from './templateStore';
import {EditorStore} from './editorStore';
import {TaskStore} from './taskStore';
import {ScriptStore} from './scriptStore';
import {ScriptToolStore} from './scriptToolStore';
import {ExternalStore} from './externalStore';

const TIME_MAP = {
    '1': ['00:00', '08:00'],
    '2': ['08:01', '16:00'],
    '3': ['16:01', '23:59']
};

class Store {
    userInfo = {}; // 用户信息
    moodListCache = []; // 接口缓存 风格列表
    timbreListCache = []; // 接口缓存 音色列表
    pubCharacterList = []; // 接口缓存 数字人公共库
    libCharacterList = []; // 接口缓存 数字人库
    ttsVoiceList = []; // 缓存接口 tts音频列表
    ttsTagList = []; // 缓存接口 tts标签列表
    ttsCustomList = []; // 接口缓存 tts克隆声音列表
    ttsCollectionList = []; // 接口缓存 tts收藏声音列表
    scriptAuth = false; // 脚本权限
    ernieAuth = false; // 数字人权限
    hasTestLiveAuth = false; // 测试直播权限
    rewriteAuth = false; // 文心口语化权限
    v3Auth = false; // 文心v3权限
    waterMaskAuth = false; // 直播水印权限
    aigcImgAuth = false; // AI图鉴权限
    templateAuth = true; // 模板权限
    hasPushLiveAuth = false; // 推流权限
    hasErnieFaqAuth = false; // 文心自动问答权限
    hasVideoPasterAuth = true; // 视频贴片禁用权限
    mountAuth = false; // 带货权限
    isDhTemplateAuth = false; // 电商模板
    // hasGuaranteeAuth = false; // 是否缴纳保证金 已与带货权限合并
    registCode = ''; // 注册码
    isAgreement = false; // 是否签署平台协议
    mediaAuth = false; // 多媒体权限（声音上传）
    riskAuth = false; // 风险权限
    customerAcquisitionAuth = false; // 获客开播权限
    sayasAuth = false; // 文案修改读音权限
    createTemplateAuth = false; // 模板生成权限
    videoDurationAuth = false; // 10min视频权限
    localLifeAuth = false; // 本地生活权限
    allLimitLiveAuth = false; // 完全限制开播权限
    partLimitLiveAuth = false;// 部分限制开播权限
    pptAuth = false;// ppt组件使用权限
    pptSmartMatchAuth = false;// ppt智能匹配权限
    swanCloneAuth = false; // 收声小程序入口展示权限
    allowLiveTime = []; // 若拥有部分限制开播权限，则允许开播的时间段
    furnishAuth = false; // 一键装修权限
    forbiddenSkipFurnish = false;// 禁止跳过一键装修
    toFaqParams = {}; // 剧本跳转问答参数
    createAIScriptParams = {};
    aigcScriptAuth = false; // AI脚本ABtest权限
    filteGoodsCardAuth = false; // 商品卡权限 用于过滤商品卡
    videoPageAuth = false;// 切镜权限
    guideType = {}; // 新手引导类型
    externalLiveInfo = {}; // 外部直播信息
    makeVideoAuth = false; // 生成视频权限
    visCharactorAuth = false; // vis检测数字人制作视频权限
    audioTTSAuth = false;
    videoClipTTSAuth = false;


    constructor() {
        makeObservable(this, {
            userInfo: observable,
            moodListCache: observable,
            timbreListCache: observable,
            pubCharacterList: observable,
            libCharacterList: observable,
            ttsVoiceList: observable,
            ttsTagList: observable,
            ttsCustomList: observable,
            ttsCollectionList: observable,
            scriptAuth: observable,
            ernieAuth: observable,
            hasTestLiveAuth: observable,
            rewriteAuth: observable,
            v3Auth: observable,
            waterMaskAuth: observable,
            aigcImgAuth: observable,
            templateAuth: observable,
            hasPushLiveAuth: observable,
            hasErnieFaqAuth: observable,
            hasVideoPasterAuth: observable,
            mountAuth: observable,
            isDhTemplateAuth: observable,
            // hasGuaranteeAuth: observable,
            registCode: observable,
            isAgreement: observable,
            mediaAuth: observable,
            toFaqParams: observable,
            sayasAuth: observable,
            videoDurationAuth: observable,
            riskAuth: observable,
            localLifeAuth: observable,
            furnishAuth: observable,
            aigcScriptAuth: observable,
            createAIScriptParams: observable,
            pptAuth: observable,
            pptSmartMatchAuth: observable,
            filteGoodsCardAuth: observable,
            videoPageAuth: observable,
            swanCloneAuth: observable,
            guideType: observable,
            externalLiveInfo: observable,
            makeVideoAuth: observable,
            visCharactorAuth: observable,
            audioTTSAuth: observable,
            videoClipTTSAuth: observable,

            fetchPreCheckAuth: action,
            fetchAuth: action,
            fetchExternalLiveInfo: action,
            getMoodListCache: action,
            getTimbreListCache: action,
            getPubCharacterList: action,
            getLibCharacterList: action,
            getTTSVoiceList: action,
            getTTSTagList: action,
            getTTSCustomList: action,
            getTTSCollectionList: action,

            setToFaqParams: action,
            getToFaqParams: action,
            adjustGoodToAiScript: action,
            fetchReserveLiveAuth: action,
            setGuideType: action
        });
        this.videoStore = new VideoStore();
        this.templateStore = new TemplateStore();
        this.EditorStore = new EditorStore();
        this.taskStore = new TaskStore();
        this.scriptStore = new ScriptStore();
        this.scriptToolStore = new ScriptToolStore();
        this.externalStore = new ExternalStore();
    }
    fetchUserInfo = flow(function* () {
        const res = (yield getUserInfo()) || {};

        if (+res?.errno === 0 && !!res?.data) {
            this.userInfo = res.data;
            window.huibo_external_auth_uk = res.data.uk || '';
        }

        return res;
    });

    fetchPreCheckAuth = flow(function* () {
        const {errno, data} = (yield checkPreAuth()) || {};

        if (+errno === 0 && !!data) {
            this.mountAuth = data.rights?.live_mount_rights;
            this.isDhTemplateAuth = data.rights?.ds_template_rights;
            // this.hasGuaranteeAuth = data.rights.guarantee_rights;
            this.registCode = data.regist_code;
            this.isAgreement = !!data.is_agreement;
        }

        return data;
    });

    fetchAuth = flow(function* () {
        const {errno, data} = (yield checkDigitalAnchorAuth()) || {};
        if (+errno === 0 && !!data) {
            // 3: true：用户在文心白名单
            // 4: true：用户不在文心黑名单
            // 5: true：用户可以用私密开播
            // 6: true：文心口语化改写
            // 8: true：预约弹窗上展示直播间水印提示
            // 9: true: 可以使用AI生图
            // 16: true: 可以展示文心自动问答授权
            // 25: true: 获客权限白名单
            this.scriptAuth = data[1];
            this.ernieAuth = data[3] && data[4];
            this.hasTestLiveAuth = data[5];
            this.rewriteAuth = data[6];
            this.v3Auth = data[7];
            this.waterMaskAuth = data[8];
            this.aigcImgAuth = data[9];
            // this.templateAuth = data[13]; 模板推全 代码暂时保留
            this.hasPushLiveAuth = data[11];
            this.hasErnieFaqAuth = data[16];
            this.hasDecouplingAuth = data[19];
            this.hasVideoPasterAuth = !data[21]; // 黑名单
            this.mediaAuth = data[24];
            this.riskAuth = data[26];// 风控权限
            this.customerAcquisitionAuth = data[29]; // 获客留资
            this.filteGoodsCardAuth = data[29]; // 获客 不使用商品卡
            this.sayasAuth = data[30];// 读音标注
            this.createTemplateAuth = data[31]; // 模板生成权限
            this.localLifeAuth = data[34]; // 本地生活
            this.personalReviewAuth = data[20]; // 人审名单
            this.videoDurationAuth = data[38]; // 视频时长放开
            this.furnishAuth = data[40]; // 一键装修权限
            this.forbiddenSkipFurnish = data[43];// 禁止跳过一键装修
            this.pptAuth = data[44]; // ppt组件
            this.pptSmartMatchAuth = data[45]; // ppt智能匹配
            this.aigcScriptAuth = data[100000] || false; // ai生成脚本权限 AI脚本A/B Test
            this.videoPageAuth = data[55]; // 视频页权限
            this.swanCloneAuth = data[62]; // 收声小程序入口权限
            this.makeVideoAuth = data[66]; // 生成视频权限
            this.visCharactorAuth = data[50]; // vis检测视频权限白名单
            this.audioTTSAuth = data[67]; // 录音白名单
            this.videoClipTTSAuth = data[68]; // 切镜录音白名单
        }
        else {
            this.scriptAuth = false;
            this.ernieAuth = false;
        }
        return data;
    });

    fetchReserveLiveAuth = flow(function* () {
        try {
            const {errno, data} = yield controlReserveLive();

            if (+errno === 0 && data) {
                this.allLimitLiveAuth = data[41]?.status === 1;
                this.partLimitLiveAuth = data[42]?.status > 0;

                if (data[41]?.status === 1) {
                    const start = moment.unix(data[41]?.start).format('YYYY年MM月DD日HH时mm分ss秒');
                    const end = moment.unix(data[41]?.end).format('YYYY年MM月DD日HH时mm分ss秒');
                    this.allowLiveTime = [start, end];
                }
                if (data[42]?.status > 0) {
                    this.allowLiveTime = TIME_MAP[data[42]?.status];
                }
            }

        }
        catch (error) {
        }

    });

    fetchExternalLiveInfo = flow(function* () {
        try {
            const {errno, data} = yield getLivePushDetail({});
            if (+errno === 0 && data) {
                this.externalLiveInfo = data.filter(item => item.push_status === 3)[0] || {};
            }
        }
        catch (error) {
        }
    });

    // 新手引导
    fetchUserGuide = flow(function* (point) {
        if (getStorage(point)) {
            return false;
        }
        try {
            const {errno, data} = yield getUserGuide({
                point
            });
            if (+errno === 0 && data) {
                if (data.show_times === 0) {
                    return true;
                }
            }
            return false;
        }
        catch (error) {
            return false;
        }
    });

    postUserGuide = flow(function* (point) {
        setStorage(point, true);
        try {
            yield setUserGuide({
                point
            });
        }
        catch (error) {
        }
    });

    getMoodListCache = flow(function* () {
        if (!this.moodListCache.length) {
            const {errno, data = []} = (yield getMoodList({
                vc_per: ''
            })) || {};

            if (+errno === 0) {
                this.moodListCache = data || [];
            }
        }
        return toJS(this.moodListCache);
    });

    getTimbreListCache = flow(function* () {
        if (!this.timbreListCache.length) {
            const {errno, data = {}} = (yield getTimbreList({
                mood: ''
            })) || {};

            if (+errno === 0) {
                const {person_list: timbreList = []} = data;

                this.timbreListCache = (timbreList[0] || []);
            }
        }
        return toJS(this.timbreListCache);
    });

    getPubCharacterList = flow(function* () {
        if (!this.pubCharacterList.length) {
            const {errno, data = {}} = yield getReourceLibList(API.GET_CHARACTER_PUB_LIST, {
                page: '1',
                rn: '100',
                order_type: '2',
                gender: 0
            });

            if (+errno === 0) {
                const {list = []} = data;

                this.pubCharacterList = (list || []);
            }
        }
        return toJS(this.pubCharacterList);
    });

    getLibCharacterList = flow(function* () {
        if (!this.libCharacterList.length) {
            const {errno, data = {}} = yield getReourceLibList(API.GET_CHARACTER_LIB_LIST, {
                page: '1',
                rn: '100',
                order_type: '2'
            });

            if (+errno === 0) {
                const {list = []} = data;

                this.libCharacterList = (list || []);
            }
        }
        return toJS(this.libCharacterList);
    });

    getTTSVoiceList = flow(function* () {
        try {
            if (!this.ttsVoiceList.length) {
                const {errno, data = {}} = yield getTTSVoice({
                    mood: '', age: 0, gender: 0
                });

                if (+errno === 0) {
                    const {list = []} = data;

                    this.ttsVoiceList = (list || []);
                }
            }
            return toJS(this.ttsVoiceList);
        }
        catch (e) {
            return [];
        }
    });

    getTTSTagList = flow(function* () {
        try {
            if (!this.ttsTagList.length) {
                const {errno, data = {}} = yield getTTSTag({attribution: 101});

                if (+errno === 0) {
                    const {list = []} = data;
                    this.ttsTagList = (list || []);
                }
            }
            return toJS(this.ttsTagList);
        }
        catch (e) {
            return [];
        }
    });

    getTTSCustomList = flow(function* (shouldUpdate = false) {
        try {
            if (!this.ttsCustomList.length || shouldUpdate) {
                const {errno, data = {}} = yield getCloneTTSVoice();

                if (+errno === 0) {
                    const {list = []} = data;

                    this.ttsCustomList = (list || []);
                }
            }
            return toJS(this.ttsCustomList);
        }
        catch (e) {
            return [];
        }
    });

    getTTSCollectionList = flow(function* (shouldUpdate = false) {
        try {
            if (!this.ttsCollectionList.length || shouldUpdate) {
                const {errno, data = {}} = yield getTTSCollectionList();

                if (+errno === 0) {
                    const {voice_list = []} = data;

                    this.ttsCollectionList = (voice_list || []);
                }
            }
            return toJS(this.ttsCollectionList);
        }
        catch (e) {
            return [];
        }
    });
    setToFaqParams(params) {
        this.faqParams = params;
    }
    getToFaqParams() {
        return toJS(this.faqParams);
    }

    adjustGoodToAiScript = flow(function* (goodId) {
        const {errno, data = {}} = (yield checkCanAIScript({goods_id: goodId})) || {};
        if (errno === 0 && data) {
            this.createAIScriptParams = {
                id: data.bmc_id,
                isDuStore: data.res
            };
        }

        return {id: data.bmc_id, isDuStore: data.res};
    });

    setGuideType(params) {
        this.guideType = params;
    }
}

export const store = new Store();
export const StoreContext = createContext(store);
export const useStore = () => useContext(StoreContext);
